import { ChevronDown } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFixedMenuPosition } from '../../hooks/useFixedMenuPosition'
import { cn } from '../../lib/utils'
import type { SelectOption } from './select-picker'

type MultiSelectPickerProps = {
  options: SelectOption[]
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
}

export function MultiSelectPicker({
  options,
  values,
  onChange,
  placeholder = 'Select options',
}: MultiSelectPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const menuStyle = useFixedMenuPosition(open, triggerRef)

  const selectedLabel = useMemo(() => {
    if (values.length === 0) {
      return placeholder
    }

    const labels = options
      .filter((option) => values.includes(option.value))
      .map((option) => option.label)

    return labels.join(', ')
  }, [options, placeholder, values])

  useEffect(() => {
    if (!open) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node

      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return
      }

      setOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const toggleValue = (optionValue: string) => {
    const next = values.includes(optionValue)
      ? values.filter((value) => value !== optionValue)
      : [...values, optionValue]

    onChange(next)
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        className="flex w-full items-center justify-between rounded-md border border-parch-200 bg-white px-3 py-2 text-left text-sm text-ink-900 hover:bg-parch-50"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={cn('truncate', values.length === 0 && 'text-ink-500')}>{selectedLabel}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-ink-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="max-h-64 overflow-y-auto rounded-md border border-parch-200 bg-white py-1 shadow-lg"
          >
            {options.map((option) => {
              const isSelected = values.includes(option.value)

              return (
                <label
                  key={option.value}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors',
                    isSelected ? 'bg-green-50 text-green-800' : 'text-ink-800 hover:bg-parch-50',
                  )}
                >
                  <input
                    type="checkbox"
                    className="rounded border-parch-300"
                    checked={isSelected}
                    onChange={() => toggleValue(option.value)}
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}
